import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
  effect
} from '@angular/core';
import { parseISO, differenceInSeconds } from 'date-fns/esm';
import { ZefScheduler } from '@zerops/zef/core';

@Component({
  selector: 'zui-relative-time',
  templateUrl: './relative-time.component.html',
  styleUrl: './relative-time.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelativeTimeComponent extends ZefScheduler {

  // # Inputs
  isFuture = input(false);
  dateTime = input<string>();

  // # Data
  // -- sync
  #dateTimeIso = computed(() => {
    const dt = this.dateTime();
    return dt ? parseISO(dt) : null;
  });

  #tick = signal(0);

  formatted = computed(() => {
    this.#tick();
    return this.#formatRelativeTime(this.#dateTimeIso(), this.isFuture());
  });

  constructor() {
    super();

    effect((onCleanup) => {
      const dateIso = this.#dateTimeIso();

      let interval: ReturnType<typeof setInterval> | undefined;

      if (dateIso) {
        interval = setInterval(() => {
          this.#tick.update(v => v + 1);
          this.renderScheduler.schedule();
        }, 1000);
      }

      onCleanup(() => {
        if (interval !== undefined) {
          clearInterval(interval);
        }
      });
    });
  }

  #formatRelativeTime(dateIso: Date | null, isFuture: boolean): string {
    if (!dateIso) {
      return isFuture ? 'in queue' : '';
    }

    const diffSeconds = Math.abs(differenceInSeconds(dateIso, new Date()));

    const days = Math.floor(diffSeconds / 86400);
    const hours = Math.floor((diffSeconds % 86400) / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;

    let formatted: string;

    if (days > 0) {
      formatted = days === 1 ? '1 day' : `${days} days`;
    } else if (hours > 0) {
      formatted = hours === 1 ? '1 hour' : `${hours} hours`;
    } else if (diffSeconds >= 300) {
      formatted = minutes === 1 ? '1 minute' : `${minutes} minutes`;
    } else if (minutes > 0) {
      const minPart = minutes === 1 ? '1 minute' : `${minutes} minutes`;
      const secPart = seconds === 1 ? '1 second' : `${seconds} seconds`;
      formatted = `${minPart} ${secPart}`;
    } else {
      formatted = seconds === 1 ? '1 second' : `${seconds} seconds`;
    }

    return isFuture ? `in ${formatted}` : `${formatted} ago`;
  }

}
